// The electricity bill as a formula — what a current sensor's units cost.
//
// The structure is MSEDCL's (Maharashtra) LT bill, line for line, and the
// defaults are the rates printed on a real 3-phase commercial bill (tariff
// 52 / LT-II Comm 3 Ph <20 kW, rates in force from 01-04-2026, MERC MYT order
// 217/2024 as reviewed in 75/2025). energyTariff.test.js rebuilds that bill —
// 825 units → ₹11,234.03 — to the paisa; change a default and it must still
// pass with the new bill, or the formula is wrong.
//
//   Fixed        ₹/month (+ ₹/kW × sanctioned load, for tariffs charged that way)
//   Energy       Σ slab units × slab rate           (one slab = a flat rate)
//   Wheeling     units × ₹/unit
//   FAC          units × ₹/unit                     (fuel adjustment, changes monthly)
//   Duty         dutyPct % × (Fixed + Energy + Wheeling + FAC)
//   ToSE         units × ₹/unit                     (Tax on Sale of Electricity)
//   ───────────
//   Bill         sum of the above
//
//   Pay by the prompt date   Bill − promptPct % × (Fixed+Energy+Wheeling+FAC)
//   Pay digitally            a further digitalPct % of that base, capped at ₹digitalCap
//   Pay late                 Bill + latePct % × Bill
//   Every payable amount is rounded to the nearest ₹10, as the bill does.
//
// ⚠️ Slab limits are MONTHLY units. A period longer or shorter than a month
// scales them (`months`) — MSEDCL prorates the same way.
//
// Nothing here knows about sensors or dates: units in, rupees out.

export const DEFAULT_TARIFF = Object.freeze({
  name: 'MSEDCL LT-II Commercial 3-phase (<20 kW)',
  fixed: 540,
  fixedPerKw: 0,
  slabs: [{ upTo: null, rate: 8.51 }],
  wheeling: 1.6,
  fac: 0.25,
  dutyPct: 21,
  tose: 0.2894,
  promptPct: 1,
  digitalPct: 0.25,
  digitalCap: 500,
  latePct: 1.25,
});

const n = (v, d = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : d;
};
const r2 = (v) => Math.round(v * 100) / 100;
export const roundTo10 = (v) => Math.round(v / 10) * 10;

/** A tariff with every field present and numeric — saved JSON may be partial or old. */
export function normalizeTariff(t) {
  const src = t && typeof t === 'object' ? t : {};
  const slabsIn = Array.isArray(src.slabs) && src.slabs.length ? src.slabs : DEFAULT_TARIFF.slabs;
  const slabs = slabsIn
    .map((s) => ({
      upTo: s && s.upTo !== null && s.upTo !== '' && s.upTo !== undefined && Number.isFinite(Number(s.upTo))
        ? Number(s.upTo) : null,
      rate: n(s && s.rate),
    }))
    // Bounded slabs ascending, the open-ended one last. Two open-ended slabs:
    // only the first means anything.
    .sort((a, b) => (a.upTo == null) - (b.upTo == null) || (a.upTo ?? 0) - (b.upTo ?? 0));
  const firstOpen = slabs.findIndex((s) => s.upTo == null);
  const clean = firstOpen === -1 ? [...slabs, { upTo: null, rate: slabs[slabs.length - 1].rate }]
    : slabs.slice(0, firstOpen + 1);
  const pick = (k) => n(src[k], DEFAULT_TARIFF[k]);
  return {
    name: typeof src.name === 'string' ? src.name : DEFAULT_TARIFF.name,
    fixed: pick('fixed'),
    fixedPerKw: pick('fixedPerKw'),
    slabs: clean,
    wheeling: pick('wheeling'),
    fac: pick('fac'),
    dutyPct: pick('dutyPct'),
    tose: pick('tose'),
    promptPct: pick('promptPct'),
    digitalPct: pick('digitalPct'),
    digitalCap: pick('digitalCap'),
    latePct: pick('latePct'),
  };
}

/** Energy charge across the slabs. -> [{ from, to, units, rate, amount }] */
export function slabBreakdown(units, slabs, months = 1) {
  const out = [];
  let left = Math.max(0, n(units));
  let prev = 0;
  for (const s of slabs) {
    if (left <= 0) break;
    const cap = s.upTo == null ? Infinity : s.upTo * months;
    const width = Math.max(0, cap - prev);
    const u = Math.min(left, width);
    if (u > 0) out.push({ from: prev, to: s.upTo == null ? null : cap, units: u, rate: s.rate, amount: u * s.rate });
    left -= u;
    prev = cap;
  }
  return out;
}

/**
 * The bill for `units`.
 * opts: { sanctionedKw = 0, months = 1 }
 * -> { units, lines: [{ key, label, local, formula, amount }], base, total,
 *      prompt, digital, payPrompt, payDigital, payNormal, late, payLate, slabs }
 */
export function billFor(units, tariff, opts = {}) {
  const t = normalizeTariff(tariff);
  const u = Math.max(0, n(units));
  const months = n(opts.months, 1) > 0 ? n(opts.months, 1) : 1;
  const kw = Math.max(0, n(opts.sanctionedKw));

  const fixed = r2((t.fixed + t.fixedPerKw * kw) * months);
  const slabs = slabBreakdown(u, t.slabs, months);
  const energy = r2(slabs.reduce((a, s) => a + s.amount, 0));
  const wheeling = r2(u * t.wheeling);
  const fac = r2(u * t.fac);
  const base = r2(fixed + energy + wheeling + fac);
  const duty = r2((base * t.dutyPct) / 100);
  const tose = r2(u * t.tose);
  const total = r2(base + duty + tose);

  const fixedFormula = t.fixedPerKw
    ? `₹${t.fixed} + ₹${t.fixedPerKw} × ${kw} kW${months !== 1 ? ` × ${months.toFixed(2)} months` : ''}`
    : `₹${t.fixed}${months !== 1 ? ` × ${months.toFixed(2)} months` : ''}`;
  const energyFormula = slabs.length <= 1
    ? `${fmtUnits(u)} × ₹${(slabs[0] ? slabs[0].rate : t.slabs[0].rate)}`
    : slabs.map((s) => `${fmtUnits(s.units)} × ₹${s.rate}`).join(' + ');

  const lines = [
    { key: 'fixed', label: 'Fixed charge', local: 'स्थिर आकार', formula: fixedFormula, amount: fixed },
    { key: 'energy', label: 'Energy charge', local: 'वीज आकार', formula: energyFormula, amount: energy },
    { key: 'wheeling', label: 'Wheeling charge', local: 'वहन आकार', formula: `${fmtUnits(u)} × ₹${t.wheeling}`, amount: wheeling },
    { key: 'fac', label: 'Fuel adjustment (FAC)', local: 'इंधन समायोजन आकार', formula: `${fmtUnits(u)} × ₹${t.fac}`, amount: fac },
    { key: 'duty', label: 'Electricity duty', local: 'वीज शुल्क', formula: `${t.dutyPct}% × ₹${base.toFixed(2)}`, amount: duty },
    { key: 'tose', label: 'Tax on sale (ToSE)', local: 'वीज विक्री कर', formula: `${fmtUnits(u)} × ₹${t.tose}`, amount: tose },
  ];

  const prompt = r2((base * t.promptPct) / 100);
  const digital = r2(Math.min(t.digitalCap, (base * t.digitalPct) / 100));
  const late = r2((total * t.latePct) / 100);

  return {
    units: u,
    lines,
    base,
    total,
    prompt,
    digital,
    late,
    payNormal: roundTo10(total),
    payPrompt: roundTo10(total - prompt),
    payDigital: roundTo10(total - prompt - digital),
    payLate: roundTo10(total + late),
    slabs,
  };
}

/** What ONE MORE unit costs at the top slab, all taxes in. Fixed charge excluded. */
export function marginalRate(tariff) {
  const t = normalizeTariff(tariff);
  const top = t.slabs[t.slabs.length - 1].rate;
  return (top + t.wheeling + t.fac) * (1 + t.dutyPct / 100) + t.tose;
}

/** The formula as one line of text, with this tariff's numbers. */
export function formulaText(tariff, sanctionedKw = 0) {
  const t = normalizeTariff(tariff);
  const fixed = t.fixedPerKw ? `(₹${t.fixed} + ₹${t.fixedPerKw}×${sanctionedKw} kW)` : `₹${t.fixed}`;
  const energy = t.slabs.length === 1
    ? `U×${t.slabs[0].rate}`
    : `slabs(U: ${t.slabs.map((s) => `${s.upTo == null ? 'rest' : `≤${s.upTo}`}@${s.rate}`).join(', ')})`;
  return `Bill = B + ${t.dutyPct}%×B + U×${t.tose},  where B = ${fixed} + ${energy} + U×${t.wheeling} + U×${t.fac}`;
}

function fmtUnits(u) {
  return Number.isInteger(u) ? String(u) : u.toFixed(1);
}
